// @flow
import {isNode, isEdge} from 'react-flow-renderer';
import {set} from 'lodash';
import {
  ACTION_NODE_MARGIN,
  ACTION_NODE_WIDTH,
  ACTION_NODE_HEIGHT_OFFSET,
  ACTION_NODE_VERTICAL_PADDING,
  ACTION_NODE_OFFSET,
} from '../SequenceDiagram/SequenceDiagram';

const isActorNode = (e) => isNode(e) && e.type === 'sequenceActor';

// These are keys defined by react-flow-renderer
type EdgeHandleKey = 'sourceHandle' | 'targetHandle';

// Each actor (the vertical bar in the diagram) has sections highlighted where the edges touch
// We need to compute which sections need to be highlighted
const highlightActors = (elements) => {
  const highlights = {};

  // Loop through the edges to find what sections of the actor we need to highlight
  const edges = elements.filter(isEdge);
  const actors = elements.filter(isActorNode).map((n) => n.id);

  const setHighlight = (e: any, handle: EdgeHandleKey) => {
    // Each edge is named according to where on the actor the edge touches
    // e.g. "2-1-right" means the edge meets the actor with id "2" at section area "1" on the right side of the line
    const EDGE_REGEX = /([a-zA-Z]*)-(\d)-([a-zA-Z]*)/;

    const match = e[handle].match(EDGE_REGEX);
    const id = match[1];
    const section = Number(match[2]);

    // Create lists of the sections that require highlights
    // SequenceDiagramActorNode will use these lists when rendering the node
    if (!highlights[id]) {
      highlights[id] = [section];
    } else if (!highlights[id].includes(section)) {
      highlights[id].push(section);
    }
  };

  edges.forEach((e) => {
    if (actors.includes(e.target)) {
      // Highlight sections on the actor where a edge ends (e.g. the arrowhead touches)
      setHighlight(e, 'targetHandle');
    } else if (actors.includes(e.source)) {
      // Highlight any sections on the actor where a edge starts (e.g. the edge line begins)
      setHighlight(e, 'sourceHandle');
    }
  });

  return highlights;
};

const positionElements = (elements, highlights) => {
  const indicies = {};
  const updatedElements = elements.map((e) => {
    // No need to position the edges since their position is calculated by react-flow-renderer
    if (isEdge(e)) {
      return e;
    }

    // Actor nodes are the vertical lines in the diagram
    if (isActorNode(e)) {
      indicies[e.id] = e.data.index;
      return {
        ...e,
        position: {x: ACTION_NODE_OFFSET * e.data.index, y: 0},
        data: {...e.data, highlights: highlights[e.id] || []},
      };
    }

    // Otherwise we have an action node and need to calculate its position between two actors
    const {from, to, row} = e.data;

    // Tracks what sequenceActor to position this sequenceAction node between
    const start = indicies[from];
    const end = indicies[to];

    const direction = end - start > 0 ? 1 : -1;

    // Find how many sequenceActor nodes this sequenceAction spans
    // If a node spans more than one segment, we need to account for the extra margin and width
    const span = end - start === 0 ? 1 : Math.abs(end - start);

    // If we are going LTR then we need to subtract the width of the node before positioning
    const widthAdjustments = direction < 0 ? span * ACTION_NODE_WIDTH : 0;
    const x =
      ACTION_NODE_OFFSET * start +
      ACTION_NODE_MARGIN * span * direction -
      widthAdjustments;
    const y =
      row * ACTION_NODE_HEIGHT_OFFSET +
      (row - 1) * ACTION_NODE_VERTICAL_PADDING;

    set(e, 'position.x', x);
    set(e, 'position.y', y);
    set(e, 'data.span', span);

    // Adjust the position if the sequenceAction node starts and ends at the same actorNode
    if (start === end) {
      set(e, 'position.x', x + ACTION_NODE_OFFSET);
      set(
        e,
        'position.y',
        y - ACTION_NODE_HEIGHT_OFFSET / 2 - ACTION_NODE_VERTICAL_PADDING / 2,
      );
    }

    return e;
  });
  return updatedElements;
};

export const positionSequenceElements = (
  nodes: Array<any>,
  elements: Array<any>,
): Array<any> => {
  const highlights = highlightActors(elements);
  return positionElements(elements, highlights);
};
